import React, { useEffect, useState } from "react";
import { GridApi } from "ag-grid-community";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import {
  MdOutlineFirstPage,
  MdOutlineLastPage,
  MdOutlineNavigateBefore,
  MdOutlineNavigateNext,
  MdOutlineTune,
} from "react-icons/md";
import { ICON_SIZES, PAGINATION_PAGE_SIZES } from "src/helpers/constants";
import CustomSelect from "../Select/CustomSelect";
import clsx from "clsx";
import useMobile from "src/hooks/useMobile";
import useModalsStore from "src/store/modalsStore";
import { useTranslation } from "react-i18next";

interface ITablePagination {
  gridApi: GridApi | null;
  resultsPerPage: number;
  noBorder?: boolean;
  showSettings?: boolean;
}

const TablePagination: React.FC<ITablePagination> = ({
  gridApi,
  resultsPerPage,
  noBorder = false,
  showSettings = true,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [rangeText, setRangeText] = useState(
    t("table_pagination.zero_results")
  );
  const { isMobile } = useMobile();
  const { setShowTableSettingsModal } = useModalsStore();

  useEffect(() => {
    if (!gridApi) return;

    const updatePagination = () => {
      const currentPage = gridApi.paginationGetCurrentPage();
      const totalPages = gridApi.paginationGetTotalPages();
      const rowCount = gridApi.paginationGetRowCount();

      const startRow = currentPage * resultsPerPage + 1;
      const endRow = Math.min(startRow + resultsPerPage - 1, rowCount);

      if (rowCount === 0) {
        setRangeText(t("table_pagination.zero_results"));
      } else {
        setRangeText(
          `${startRow}-${endRow} ${t("table_pagination.of")} ${rowCount}`
        );
      }

      setCurrentPage(currentPage);
      setTotalPages(totalPages);
      setRowCount(rowCount);
    };

    gridApi?.addEventListener?.("paginationChanged", updatePagination);
    updatePagination();

    return () => {
      if (gridApi?.isDestroyed?.()) return;
      gridApi?.removeEventListener?.("paginationChanged", updatePagination);
    };
  }, [gridApi, resultsPerPage]);

  if (!gridApi) return null;

  return (
    <div
      className={clsx([
        "flex",
        "flex-col",
        "md:flex-row",
        "items-center",
        "justify-between",
        "py-2",
        {
          "border-t": !noBorder,
        },
        "dark:border-white/15",
        "gap-4",
        "pt-4",
      ])}
    >
      <div
        className={clsx([
          "flex",
          "flex-row",
          "items-center",
          "gap-2",
          "justify-between",
          "md:justify-start",
          "w-full",
          "md:w-auto",
        ])}
      >
        <div
          className={clsx([
            "flex",
            "flex-row",
            "items-center",
            "gap-2",
            "justify-start",
            "w-auto",
          ])}
        >
          <div className={clsx(["text-sm", "md:text-sm", "whitespace-nowrap"])}>
            {t("table_pagination.rows")}:
          </div>
          <div
            className={clsx([
              "w-[75px]",
              "custom-pagination-selector-container",
            ])}
          >
            <CustomSelect
              options={PAGINATION_PAGE_SIZES.map?.((pageSizeItem: number) => ({
                value: `${pageSizeItem}`,
                label: `${pageSizeItem}`,
              }))}
              isClearable={false}
              isSearchable={false}
              onChange={(e) => {
                const newSize = Number(e.value);
                gridApi.setGridOption("paginationPageSize", newSize);
              }}
              value={`${resultsPerPage}`}
            />
          </div>
        </div>
        <div className={clsx(["text-sm", "md:text-sm", "whitespace-nowrap"])}>
          {rangeText}
        </div>
      </div>
      <div
        className={clsx([
          "flex",
          "gap-1",
          "md:gap-2",
          "items-center",
          "justify-between",
          "md:justify-end",
          "w-full",
        ])}
      >
        <ButtonGroup
          gap={1}
          buttons={[
            {
              icon: <MdOutlineFirstPage size={ICON_SIZES.MD} />,
              disabled: currentPage === 0,
              onClick: () => gridApi.paginationGoToFirstPage(),
              tooltip: t("table_pagination.first"),
            },
            {
              icon: <MdOutlineNavigateBefore size={ICON_SIZES.MD} />,
              disabled: currentPage === 0,
              onClick: () => gridApi.paginationGoToPreviousPage(),
              tooltip: t("table_pagination.previous"),
            },
          ]}
        />

        <div
          className={clsx([
            "text-sm",
            "md:text-sm",
            "min-w-[50px]",
            "w-full",
            "md:w-auto",
            "md:min-w-[100px]",
            "text-center",
          ])}
        >
          {totalPages === 0 ? (
            <>
              {isMobile
                ? t("table_pagination.empty")
                : t("table_pagination.zero_pages")}
            </>
          ) : (
            <>
              {isMobile ? (
                <>
                  {currentPage + 1}-{totalPages}
                </>
              ) : (
                <>
                  {currentPage + 1} {t("table_pagination.of")} {totalPages}
                </>
              )}
            </>
          )}
        </div>

        <ButtonGroup
          gap={1}
          buttons={[
            {
              icon: <MdOutlineNavigateNext size={ICON_SIZES.MD} />,
              disabled: currentPage >= totalPages - 1,
              onClick: () => gridApi.paginationGoToNextPage(),
              tooltip: t("table_pagination.next"),
            },
            {
              icon: <MdOutlineLastPage size={ICON_SIZES.MD} />,
              disabled: currentPage >= totalPages - 1,
              onClick: () => gridApi.paginationGoToLastPage(),
              tooltip: t("table_pagination.last"),
            },
            {
              icon: <MdOutlineTune size={ICON_SIZES.MD} />,
              tooltip: t("table_pagination.customize"),
              onClick: () => setShowTableSettingsModal(true),
              hide: !showSettings,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default React.memo(TablePagination);
